import React, { useState, useEffect } from 'react';
import { Database, Table, Search, ChevronRight, ChevronDown, Eye, Copy, ArrowLeft } from 'lucide-react';

const AdminTableBrowserPage = () => {
    const [configurations, setConfigurations] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [columnLoading, setColumnLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedTables, setExpandedTables] = useState(new Set());

    // Load configurations on mount
    useEffect(() => {
        loadConfigurations();
    }, []);

    const loadConfigurations = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/admin/database/configurations');
            const result = await response.json();

            if (response.ok) {
                setConfigurations(result.configurations || []);
            } else {
                console.error('Failed to load configurations:', result.error);
            }
        } catch (error) {
            console.error('Network error loading configurations:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadTables = async (configId) => {
        setTableLoading(true);
        setTables([]);
        setSelectedTable(null);
        setColumns([]);

        try {
            const response = await fetch(`/api/admin/database/configurations/${configId}/tables`);
            const result = await response.json();

            if (response.ok) {
                setTables(result.tables || []);
            } else {
                alert('Failed to load tables: ' + (result.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Network error loading tables: ' + error.message);
        } finally {
            setTableLoading(false);
        }
    };

    const loadColumns = async (configId, tableName) => {
        setColumnLoading(true);
        setColumns([]);

        try {
            const response = await fetch(`/api/admin/database/configurations/${configId}/tables/${encodeURIComponent(tableName)}/columns`);
            const result = await response.json();

            if (response.ok) {
                setColumns(result.columns || []);
                setSelectedTable(tableName);
            } else {
                alert('Failed to load columns: ' + (result.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Network error loading columns: ' + error.message);
        } finally {
            setColumnLoading(false);
        }
    };

    const selectConfiguration = (config) => {
        setSelectedConfig(config);
        setTables([]);
        setSelectedTable(null);
        setColumns([]);
        setExpandedTables(new Set());
        loadTables(config.id);
    };

    const toggleTableExpansion = (tableName) => {
        const newExpanded = new Set(expandedTables);
        if (newExpanded.has(tableName)) {
            newExpanded.delete(tableName);
            if (selectedTable === tableName) {
                setSelectedTable(null);
                setColumns([]);
            }
        } else {
            newExpanded.add(tableName);
            loadColumns(selectedConfig.id, tableName);
        }
        setExpandedTables(newExpanded);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            // Could show a toast notification here
        });
    };

    const getTypeColor = (type) => {
        const lowerType = type.toLowerCase();
        if (lowerType.includes('int') || lowerType.includes('number') || lowerType.includes('decimal') || lowerType.includes('float')) {
            return 'bg-blue-100 text-blue-800';
        } else if (lowerType.includes('varchar') || lowerType.includes('text') || lowerType.includes('char') || lowerType.includes('string')) {
            return 'bg-green-100 text-green-800';
        } else if (lowerType.includes('date') || lowerType.includes('time') || lowerType.includes('timestamp')) {
            return 'bg-purple-100 text-purple-800';
        } else if (lowerType.includes('bool') || lowerType.includes('bit')) {
            return 'bg-yellow-100 text-yellow-800';
        } else {
            return 'bg-gray-100 text-gray-800';
        }
    };

    const filteredTables = tables.filter(table =>
        table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        table.schema.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const goBack = () => {
        setSelectedConfig(null);
        setTables([]);
        setSelectedTable(null);
        setColumns([]);
        setExpandedTables(new Set());
        setSearchTerm('');
    };

    return (
        <div>
            <div className="mb-6">
                <div className="flex items-center gap-4 mb-2">
                    {selectedConfig && (
                        <button
                            onClick={goBack}
                            className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
                        >
                            <ArrowLeft size={16} />
                            Back to Configurations
                        </button>
                    )}
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Table size={24} />
                        {selectedConfig ? `${selectedConfig.name} - Table Browser` : 'Database Table Browser'}
                    </h1>
                </div>
                <p className="text-gray-600">
                    {selectedConfig
                        ? `Browse tables and columns in your ${selectedConfig.type} database`
                        : 'Select a database configuration to explore its tables and columns'
                    }
                </p>
            </div>

            {!selectedConfig ? (
                /* Configuration Selection */
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h2 className="text-lg font-semibold text-gray-900">Select Database Configuration</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Choose a database configuration to explore its tables and columns.
                        </p>
                    </div>

                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin mx-auto mb-4 w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                            <p className="text-gray-600">Loading configurations...</p>
                        </div>
                    ) : configurations.length === 0 ? (
                        <div className="p-12 text-center">
                            <Database className="mx-auto mb-4 text-gray-400" size={48} />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No configurations found</h3>
                            <p className="text-gray-600">
                                Create a database configuration first to explore tables.
                            </p>
                        </div>
                    ) : (
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {configurations.map((config) => (
                                    <div
                                        key={config.id}
                                        onClick={() => selectConfiguration(config)}
                                        className="p-4 border rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="font-medium text-gray-900">{config.name}</h3>
                                            <ChevronRight size={16} className="text-gray-400" />
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>
                                                    {config.type}
                                                </span>
                                            </div>
                                            <p>{config.host}:{config.port}</p>
                                            <p>{config.database}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                /* Table Browser */
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tables List */}
                    <div className="bg-white rounded-lg shadow-sm border">
                        <div className="p-4 border-b">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-lg font-semibold text-gray-900">Tables</h2>
                                <div className="text-sm text-gray-500">
                                    {tables.length} table{tables.length !== 1 ? 's' : ''} found
                                </div>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Search tables..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {tableLoading ? (
                                <div className="p-8 text-center">
                                    <div className="animate-spin mx-auto mb-4 w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                                    <p className="text-gray-600">Loading tables...</p>
                                </div>
                            ) : filteredTables.length === 0 ? (
                                <div className="p-8 text-center">
                                    <Table className="mx-auto mb-4 text-gray-400" size={32} />
                                    <p className="text-gray-600">
                                        {searchTerm ? 'No tables match your search' : 'No tables found in this database'}
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {filteredTables.map((table) => (
                                        <div
                                            key={table.fullName}
                                            className={`p-3 cursor-pointer hover:bg-gray-50 ${
                                                selectedTable === table.fullName ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                                            }`}
                                            onClick={() => toggleTableExpansion(table.fullName)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    {expandedTables.has(table.fullName) ? (
                                                        <ChevronDown size={16} className="text-gray-400" />
                                                    ) : (
                                                        <ChevronRight size={16} className="text-gray-400" />
                                                    )}
                                                    <div>
                                                        <div className="font-medium text-gray-900">{table.name}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {table.schema !== table.name && `${table.schema}.`}
                                                            {table.type && ` • ${table.type}`}
                                                            {table.owner && ` • ${table.owner}`}
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyToClipboard(table.fullName);
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-gray-600"
                                                    title="Copy table name"
                                                >
                                                    <Copy size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Columns Details */}
                    <div className="bg-white rounded-lg shadow-sm border">
                        <div className="p-4 border-b">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {selectedTable ? `Columns in ${selectedTable}` : 'Column Details'}
                            </h2>
                            {selectedTable && (
                                <p className="text-sm text-gray-500 mt-1">
                                    {columns.length} column{columns.length !== 1 ? 's' : ''} found
                                </p>
                            )}
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {columnLoading ? (
                                <div className="p-8 text-center">
                                    <div className="animate-spin mx-auto mb-4 w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                                    <p className="text-gray-600">Loading columns...</p>
                                </div>
                            ) : !selectedTable ? (
                                <div className="p-8 text-center">
                                    <Eye className="mx-auto mb-4 text-gray-400" size={32} />
                                    <p className="text-gray-600">Select a table to view its columns</p>
                                </div>
                            ) : columns.length === 0 ? (
                                <div className="p-8 text-center">
                                    <p className="text-gray-600">No columns found in this table</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {columns.map((column, index) => (
                                        <div key={index} className="p-3 hover:bg-gray-50">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="font-medium text-gray-900">{column.name}</div>
                                                <button
                                                    onClick={() => copyToClipboard(column.name)}
                                                    className="p-1 text-gray-400 hover:text-gray-600"
                                                    title="Copy column name"
                                                >
                                                    <Copy size={14} />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(column.type)}`}>
                                                    {column.type}
                                                    {column.maxLength && `(${column.maxLength})`}
                                                    {column.precision && column.scale && `(${column.precision},${column.scale})`}
                                                </span>
                                                {!column.nullable && (
                                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        NOT NULL
                                                    </span>
                                                )}
                                                {column.key && (
                                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        {column.key}
                                                    </span>
                                                )}
                                            </div>
                                            {column.default && (
                                                <div className="text-xs text-gray-500">
                                                    Default: {column.default}
                                                </div>
                                            )}
                                            {column.extra && (
                                                <div className="text-xs text-gray-500">
                                                    {column.extra}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTableBrowserPage;